import React, { useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateAuthorizedDto,
  GetAuthorizedDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  convert,
  validate,
} from '../../../common/pokCore/contexts/AuthorizedContext';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { AuthorizedSearch } from '../../components/Authorized/AuthorizedSearch';
import { AuthorizedModalEditor } from '../../components/Authorized/AuthorizedModalEditor';

export const AuthorizedView: React.FC = () => {
  const pok = usePokCore();
  const [authorized, setAuthorized] = useState<GetAuthorizedDto>();
  const [showNew, setShowNew] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const { t, tk } = useTranslation('administrationLng');

  const handleAdd = () => {
    setShowNew(true);
    setAuthorized(undefined);
  };

  const addEditAuthorized = async (
    authorizedCU: CreateUpdateAuthorizedDto,
    id?: string,
  ) => {
    const status = await validate(authorizedCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.authorized.update(id, authorizedCU);
        } else {
          await pok.authorized.create(authorizedCU);
        }
        setShowNew(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleCloseNew = () => {
    setShowNew(false);
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.authorized.getAllByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.authorized],
  );

  const getCount = useMemo(
    () =>
      ((filterText: string, filterColumns) =>
        pok.authorized.getAllCount(
          filterText,
          filterColumns,
        )) as GridCountFunction,
    [pok.authorized],
  );

  const handleClick = async (id: string) => {
    const getAuthorized = await pok.authorized.getById(id);
    setAuthorized(getAuthorized);
    setShowNew(true);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.common.authorized)}</Card.Title>
        <Button variant="outline-primary" onClick={handleAdd}>
          {t(tk.administration.authorizedNew)}
        </Button>
        <AuthorizedSearch
          getData={getData}
          getCount={getCount}
          onRowClick={handleClick}
          refresh={refresh}
        />
        <AuthorizedModalEditor
          id={authorized?.id}
          systemNumber={authorized?.systemNumber}
          authorized={convert(authorized)}
          onCompleted={addEditAuthorized}
          show={showNew}
          handleClose={handleCloseNew}
        />
      </Card.Body>
    </Card>
  );
};
